import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, Star } from 'lucide-react';
import { PRICING_PLANS } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const PLAN_NAME_KEYS = ['plan.l2.name', 'plan.l3.name', 'plan.l47.name'];
const PLAN_DESC_KEYS  = ['plan.l2.desc', 'plan.l3.desc', 'plan.l47.desc'];
const PLAN_FEATURE_KEYS = [
  ['plan.feature.materials', 'plan.feature.tutorSupport', 'plan.feature.6months', 'plan.feature.digitalCert', 'plan.feature.payment'],
  ['plan.feature.materials', 'plan.feature.tutorSupport', 'plan.feature.12months', 'plan.feature.ofqualCert', 'plan.feature.payment', 'plan.feature.portal', 'plan.feature.career'],
  ['plan.feature.materials', 'plan.feature.seniorTutor', 'plan.feature.18months', 'plan.feature.ofqualCert', 'plan.feature.payment', 'plan.feature.portal', 'plan.feature.progression', 'plan.feature.priority'],
];

export default function Pricing() {
  const { t } = useSiteLanguage();

  const PAYMENT_OPTIONS = [
    { titleKey: 'pricing.payment.full.title',        descKey: 'pricing.payment.full.desc' },
    { titleKey: 'pricing.payment.deposit.title',     descKey: 'pricing.payment.deposit.desc' },
    { titleKey: 'pricing.payment.installments.title', descKey: 'pricing.payment.installments.desc' },
  ];

  const FAQS_PRICING = [
    { q: t('pricing.faq1.q'), a: t('pricing.faq1.a') },
    { q: t('pricing.faq2.q'), a: t('pricing.faq2.a') },
    { q: t('pricing.faq3.q'), a: t('pricing.faq3.a') },
    { q: t('pricing.faq4.q'), a: t('pricing.faq4.a') },
  ];

  return (
    <div className="min-h-screen">
      <Seo
        title="Fees & Pricing"
        description="Clear, upfront course fees with flexible instalment plans. See what's included with every Atlas Academy online qualification."
        path="/pricing"
      />
      {/* Hero */}
      <div className="bg-[#0f2b4a] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-300 text-sm font-medium mb-2">{t('pricing.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-5xl font-extrabold mb-4">{t('pricing.hero.title')}</h1>
          <p className="text-blue-100/70 max-w-xl text-lg leading-relaxed">
            {t('pricing.hero.desc')}
          </p>
        </div>
      </div>

      {/* Pricing cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('pricing.cards.title')}</h2>
            <p className="text-gray-500">{t('pricing.cards.desc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING_PLANS.map((plan, idx) => (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl border-2 p-6 relative ${
                  plan.popular ? 'border-blue-600 shadow-lg' : 'border-gray-100 shadow-sm'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                      <Star size={10} fill="white" /> {t('pricing.cards.popular')}
                    </span>
                  </div>
                )}
                <div className="mb-5">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{t(PLAN_NAME_KEYS[idx], plan.name)}</h3>
                  <p className="text-gray-500 text-sm">{t(PLAN_DESC_KEYS[idx], plan.description)}</p>
                </div>
                <div className="mb-5">
                  <p className="text-3xl font-extrabold text-gray-900">
                    £{plan.priceFrom}
                    <span className="text-base font-medium text-gray-400"> – £{plan.priceTo}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{t('pricing.cards.priceNote')}</p>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {PLAN_FEATURE_KEYS[idx].map(fKey => (
                    <li key={fKey} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle size={15} className="text-blue-600 shrink-0 mt-0.5" />
                      {t(fKey)}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/courses"
                  className={`block text-center py-3 rounded-lg font-semibold text-sm transition-colors ${
                    plan.popular
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                  }`}
                >
                  {t('pricing.cards.popular') ? t(PLAN_NAME_KEYS[idx], plan.name) : plan.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment plans info */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide mb-2">{t('pricing.payment.eyebrow')}</p>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-5">{t('pricing.payment.title')}</h2>
              <div className="space-y-5">
                {PAYMENT_OPTIONS.map(opt => (
                  <div key={opt.titleKey} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                      <CheckCircle size={15} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{t(opt.titleKey)}</p>
                      <p className="text-gray-500 text-sm mt-0.5">{t(opt.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-2">{t('pricing.discuss.title')}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                {t('pricing.discuss.desc')}
              </p>
              <div className="space-y-3">
                <a href="tel:+442033555646" className="flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg text-sm font-semibold transition-colors">
                  <Phone size={16} /> {t('pricing.discuss.call')}
                </a>
                <Link to="/contact" className="flex items-center gap-3 border border-gray-200 hover:bg-gray-100 text-gray-700 px-5 py-3 rounded-lg text-sm font-medium transition-colors">
                  <ArrowRight size={16} /> {t('pricing.discuss.enquiry')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ about fees */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">{t('pricing.faqTitle')}</h2>
          <div className="space-y-4">
            {FAQS_PRICING.map(item => (
              <div key={item.q} className="bg-white rounded-xl p-5 border border-gray-100">
                <p className="font-semibold text-gray-900 text-sm mb-2">{item.q}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f2b4a] py-14">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{t('pricing.cta.title')}</h2>
          <p className="text-blue-200/70 mb-7 max-w-md mx-auto">{t('pricing.cta.desc')}</p>
          <Link to="/courses" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3.5 rounded-lg font-semibold transition-colors">
            {t('pricing.cta.btn')} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
